/**
 * Whether a request method mutates, and therefore must declare a JSON
 * content-type.
 *
 * The Go services guard every mutation with a CSRF check that rejects anything
 * whose content-type is not `application/json` — including bodyless requests. A
 * `DELETE` with nothing to send still has to say it is JSON, which is easy to get
 * wrong when the header is derived from the presence of a body instead of from the
 * method.
 *
 * A method-based rule cannot drift the way "does this call happen to have a body"
 * does: adding a bodyless mutation is now correct by default.
 */
export function isMutation(method?: string): boolean {
  // No method means fetch defaults to GET.
  if (!method) return false
  return method.toUpperCase() !== 'GET'
}

/**
 * An error carrying what the server actually said.
 *
 * `status` and `meta` matter because some refusals are only actionable with
 * them — the earliest acceptable backfill date arrives in `meta`, and dropping
 * it turns a fixable refusal into an unexplainable one.
 */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

/**
 * The one fetch wrapper.
 *
 * There were six — auth, health, wiki, reels, keys, jobs — each carrying the
 * same comment about the CSRF guard and two different implementations
 * underneath. The split was not cosmetic:
 *
 *   headers: isMutation(...) ? { 'content-type': 'application/json' } : undefined,
 *   ...init,          // reels, jobs, health, auth
 *
 * puts the caller's `init` LAST, so any call passing `headers` replaced the
 * computed object wholesale and lost `content-type` — the exact header the
 * guard rejects a mutation for missing. wiki and keys spread `...init` first
 * and merged, which is correct. Four copies were wrong and two were right, and
 * one of the wrong ones carried a comment saying it was "kept in sync" with
 * another that had already diverged from it.
 *
 * Here `...init` comes first and `headers` is computed last from both sides, so
 * a caller can add a header without silently removing the one the server
 * requires.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET'
  // A FormData body is multipart: the browser writes its content-type, with
  // the boundary, and a route that takes one guards itself without the JSON
  // rule. Naming JSON over it would break the body.
  const multipart = init?.body instanceof FormData
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers:
      isMutation(method) && !multipart
        ? { 'content-type': 'application/json', ...init?.headers }
        : init?.headers,
  })

  // 204 first: there is no body to parse, and asking for one throws.
  if (res.status === 204) return undefined as T

  const body = (await res.json().catch(() => ({}))) as {
    error?: { type?: string; meta?: Record<string, unknown> | null }
  } & T

  if (!res.ok) {
    // The code, not a sentence — lib/errors.ts turns it into one at the edge
    // where a person reads it. Status and meta ride along because some refusals
    // cannot be explained without them.
    throw new HttpError(
      res.status,
      body.error?.type ?? `request failed (${res.status})`,
      body.error?.meta ?? undefined,
    )
  }
  return body
}
